import type { ChatTaskContext } from "../types";

export const TASK_OUTPUT_DIALOG_PAYLOAD_KEY_PREFIX = "__finster_task_output_dialog_data__:";
export const TASK_OUTPUT_DIALOG_OPENER_PAYLOAD_PROPERTY = "__finsterTaskOutputDialogPayload__";
export const TASK_OUTPUT_DIALOG_RESULT_KIND = "finster.taskOutputDialog.result";
export const TASK_OUTPUT_DIALOG_SESSION_QUERY_PARAM = "sessionId";

export interface TaskOutputDialogInitialPayload {
  sessionId: string;
  userEmail: string;
}

export interface TaskOutputDialogResultPayload {
  selection: ChatTaskContext | null;
}

export interface TaskOutputDialogResultEnvelope {
  kind: typeof TASK_OUTPUT_DIALOG_RESULT_KIND;
  sessionId: string;
  payload: TaskOutputDialogResultPayload;
}

export function isTaskOutputDialogSessionId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= 128 &&
    /^[A-Za-z0-9._-]+$/.test(value)
  );
}

export function taskOutputDialogPayloadKey(sessionId: string): string {
  return `${TASK_OUTPUT_DIALOG_PAYLOAD_KEY_PREFIX}${sessionId}`;
}

export function createTaskOutputDialogSessionId(): string {
  try {
    const cryptoApi = crypto as Crypto & { randomUUID?: () => string };
    if (typeof cryptoApi.randomUUID === "function") return cryptoApi.randomUUID();
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

export function isTaskOutputDialogInitialPayload(
  value: unknown
): value is TaskOutputDialogInitialPayload {
  if (!value || typeof value !== "object") return false;
  const candidate = value as { sessionId?: unknown; userEmail?: unknown };
  return (
    isTaskOutputDialogSessionId(candidate.sessionId) &&
    typeof candidate.userEmail === "string" &&
    candidate.userEmail.trim().length > 0
  );
}

export function normalizeTaskOutputDialogSelection(value: unknown): ChatTaskContext | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as {
    content?: unknown;
    name?: unknown;
    citations?: unknown;
  };
  if (
    typeof candidate.content !== "string" ||
    !candidate.content.trim() ||
    typeof candidate.name !== "string" ||
    !candidate.name.trim() ||
    !Array.isArray(candidate.citations) ||
    !candidate.citations.every(
      (citation) => !!citation && typeof citation === "object" && !Array.isArray(citation)
    )
  ) {
    return null;
  }
  return {
    content: candidate.content,
    name: candidate.name,
    citations: candidate.citations as ChatTaskContext["citations"],
  };
}

export function isTaskOutputDialogResultEnvelope(
  value: unknown
): value is TaskOutputDialogResultEnvelope {
  if (!value || typeof value !== "object") return false;
  const candidate = value as {
    kind?: unknown;
    sessionId?: unknown;
    payload?: { selection?: unknown } | null;
  };
  if (
    candidate.kind !== TASK_OUTPUT_DIALOG_RESULT_KIND ||
    !isTaskOutputDialogSessionId(candidate.sessionId) ||
    !candidate.payload ||
    typeof candidate.payload !== "object" ||
    !Object.prototype.hasOwnProperty.call(candidate.payload, "selection")
  ) {
    return false;
  }
  return (
    candidate.payload.selection === null ||
    normalizeTaskOutputDialogSelection(candidate.payload.selection) !== null
  );
}
